/**
 * 模板注册表
 * 集中管理所有模板的导入和注册
 * 避免循环依赖，提供统一的模板初始化入口
 */

import { TemplateManager } from './index';

// 导入所有模板配置
import { academicHexagramConfig } from './configs/academic-hexagram';
import { activeReunionConfig } from './configs/active-reunion';
import { ambiguousSpreadConfig } from './configs/ambiguous-spread';
import { careerDevelopmentConfig } from './configs/career-development';
import { careerPlanningConfig } from './configs/career-planning';
import { industryFitSpreadConfig } from './configs/industry-fit-spread';
import { jobContinueSpreadConfig } from './configs/job-continue-spread';
import { jobHuntSpreadConfig } from './configs/job-hunt-spread';
import { compoundCheckConfig } from './configs/compound-check';
import { compoundCrossConfig } from './configs/compound-cross';
import { destinyWheelConfig } from './configs/destiny-wheel';
import { developmentSpreadConfig } from './configs/development-spread';
import { entrepreneurshipSpreadConfig } from './configs/entrepreneurship-spread';
import { exPartnerReconciliationConfig } from './configs/ex-partner-reconciliation';
import { exPartnerStalkingSpreadConfig } from './configs/ex-partner-stalking-spread';
import { examPreparationConfig } from './configs/exam-preparation';
import { feelingsThoughtsConfig } from './configs/feelings-thoughts';
import { crushSpreadConfig } from './configs/crush-spread';
import { cheatingCheckSpreadConfig } from './configs/cheating-check-spread';
import { sincereHeartSpreadConfig } from './configs/sincere-heart-spread';
import { fortuneNearMidFarConfig } from './configs/fortune-near-mid-far';
import { fourSeasonsSpreadConfig } from './configs/four-seasons-spread';
import { friendshipEnergyScanConfig } from './configs/friendship-energy-scan';
import { futureLoverSpreadConfig } from './configs/future-lover-spread';
import { generalYesNoConfig } from './configs/general-yes-no';
import { giveUpSpreadConfig } from './configs/give-up-spread';
import { heartVoiceSpreadConfig } from './configs/heart-voice-spread';
import { learningStatusAdviceConfig } from './configs/learning-status-advice';
import { learningProblemSolvingConfig } from './configs/learning-problem-solving';
import { longingSpreadConfig } from './configs/longing-spread';
import { loveProspectConfig } from './configs/love-prospect';
import { multiDimensionalThoughtsConfig } from './configs/multi-dimensional-thoughts';
import { newLovePredictionConfig } from './configs/new-love-prediction';
import { newSemesterSpreadConfig } from './configs/new-semester-spread';
import { relationshipDevelopmentThreeMonthsConfig } from './configs/relationship-development-three-months';
import { soulmate3CardsConfig } from './configs/soulmate-3-cards';
import { soulmate6CardsConfig } from './configs/soulmate-6-cards';
import { soulmate9CardsConfig } from './configs/soulmate-9-cards';
import { secretLoveConfig } from './configs/secret-love';
import { thoughtsDevelopmentConfig } from './configs/thoughts-development';
import { twoChoiceConfig } from './configs/two-choice';
import { venusSpreadConfig } from './configs/venus-spread';
import { peekInnerHeartConfig } from './configs/peek-inner-heart';
import { peachBlossomBloomConfig } from './configs/peach-blossom-bloom';
import { peachBlossomSpreadConfig } from './configs/peach-blossom-spread';
import { wealthFortuneSpreadConfig } from './configs/wealth-fortune-spread';
import { christmasGuidanceConfig } from './configs/christmas-guidance';
import { christmasLoveConfig } from './configs/christmas-love';
import { unemploymentTurnaroundSpreadConfig } from './configs/unemployment-turnaround-spread';
import { workplaceBottleneckSpreadConfig } from './configs/workplace-bottleneck-spread';

/**
 * 初始化所有模板配置
 * 在前后端应用启动时调用，确保模板系统正确初始化
 */
export function initializeTemplates(): void {
  if (TemplateManager.isInitialized()) {
    return; // 避免重复初始化
  }

  // 注册所有模板
  const templateConfigs = [
    compoundCheckConfig,
    compoundCrossConfig,
    destinyWheelConfig,
    feelingsThoughtsConfig,
    soulmate3CardsConfig,
    soulmate6CardsConfig,
    soulmate9CardsConfig,
    fortuneNearMidFarConfig,
    wealthFortuneSpreadConfig,
    christmasGuidanceConfig,
    christmasLoveConfig,
    fourSeasonsSpreadConfig,
    friendshipEnergyScanConfig,
    loveProspectConfig,
    relationshipDevelopmentThreeMonthsConfig,
    academicHexagramConfig,
    activeReunionConfig,
    careerDevelopmentConfig,
    careerPlanningConfig,
    industryFitSpreadConfig,
    jobContinueSpreadConfig,
    jobHuntSpreadConfig,
    examPreparationConfig,
    twoChoiceConfig,
    ambiguousSpreadConfig,
    developmentSpreadConfig,
    entrepreneurshipSpreadConfig,
    unemploymentTurnaroundSpreadConfig,
    workplaceBottleneckSpreadConfig,
    newLovePredictionConfig,
    newSemesterSpreadConfig,
    multiDimensionalThoughtsConfig,
    generalYesNoConfig,
    learningStatusAdviceConfig,
    learningProblemSolvingConfig,
    longingSpreadConfig,
    crushSpreadConfig,
    cheatingCheckSpreadConfig,
    sincereHeartSpreadConfig,
    secretLoveConfig,
    thoughtsDevelopmentConfig,
    venusSpreadConfig,
    futureLoverSpreadConfig,
    exPartnerReconciliationConfig,
    exPartnerStalkingSpreadConfig,
    giveUpSpreadConfig,
    heartVoiceSpreadConfig,
    peekInnerHeartConfig,
    peachBlossomBloomConfig,
    peachBlossomSpreadConfig
  ];

  TemplateManager.registerAll(templateConfigs);

  // 标记已初始化
  TemplateManager.markInitialized();

  // 调试信息（开发阶段）
  if (typeof globalThis !== 'undefined' && (globalThis as any).process?.env?.NODE_ENV === 'development') {
    console.log(`[TemplateRegistry] Initialized ${TemplateManager.getTemplateCount()} templates`);
  }
}

/**
 * 获取模板注册统计信息
 */
export function getRegistrationStats(): {
  totalTemplates: number;
  templatesWithDisabledTitle: number;
  templatesWithDisabledCustomerQuestion: number;
  templatesWithDisabledCardCount: number;
  isInitialized: boolean;
} {
  return {
    totalTemplates: TemplateManager.getTemplateCount(),
    templatesWithDisabledTitle: TemplateManager.getTemplatesWithDisabledTitle().length,
    templatesWithDisabledCustomerQuestion: TemplateManager.getTemplatesWithDisabledCustomerQuestion().length,
    templatesWithDisabledCardCount: TemplateManager.getTemplatesWithDisabledCardCount().length,
    isInitialized: TemplateManager.isInitialized()
  };
}

/**
 * 重新初始化模板系统（主要用于测试和开发）
 */
export function reinitializeTemplates(): void {
  TemplateManager.clear();
  initializeTemplates();
}
